import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Upload, 
  Search, 
  Download,
  Loader,
  Image as ImageIcon,
  Monitor,
  Smartphone,
  Tablet
} from 'lucide-react';
import { UnsplashService } from './UnsplashService';

interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string;
  user: {
    name: string;
    username: string;
  };
  width: number;
  height: number;
}

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelect: (imageUrl: string, imageData?: any) => void;
  isGlobalDarkMode: boolean;
  title?: string;
  acceptedFormats?: string[];
  maxFileSize?: number; // in MB
  showUnsplash?: boolean;
  initialSearchQuery?: string;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onImageSelect,
  isGlobalDarkMode,
  title = "Upload Image",
  acceptedFormats = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
  maxFileSize = 10, // 10MB default
  showUnsplash = true,
  initialSearchQuery = ""
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'unsplash'>('upload');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [unsplashImages, setUnsplashImages] = useState<UnsplashImage[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Handle file upload from device
  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (!file) return;

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFormats.includes(fileExtension)) {
      alert(`Please upload a valid image file. Supported formats: ${acceptedFormats.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > maxFileSize * 1024 * 1024) {
      alert(`File size must be less than ${maxFileSize}MB`);
      return;
    }

    setIsUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
               const result = e.target?.result as string;
       onImageSelect(result, {
          name: file.name,
          size: file.size,
          type: file.type,
          source: 'upload',
          file: file
        });
        setIsUploading(false);
        onClose();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload image. Please try again.');
      setIsUploading(false);
    }
  }, [acceptedFormats, maxFileSize, onImageSelect, onClose]);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  // Search Unsplash images
  const searchUnsplashImages = useCallback(async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setUnsplashImages([]);
      return;
    }

    setIsSearching(true);
    
    try {
      const response = await UnsplashService.searchPhotos(query, page);
      
      if (page === 1) {
        setUnsplashImages(response.results);
      } else {
        setUnsplashImages(prev => [...prev, ...response.results]);
      }
      
      setHasMore(page < response.total_pages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching Unsplash:', error);
      if (page === 1) {
        setUnsplashImages([]);
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchUnsplashImages(value, 1);
    }, 500);
  }, [searchUnsplashImages]);

  // Load more images
  const loadMoreImages = useCallback(() => {
    if (!isSearching && hasMore && searchQuery.trim()) {
      searchUnsplashImages(searchQuery, currentPage + 1);
    }
  }, [isSearching, hasMore, searchQuery, currentPage, searchUnsplashImages]);

  // Handle Unsplash image selection
  const handleUnsplashSelect = useCallback(async (image: UnsplashImage) => {
    setSelectedImageId(image.id);
    
    try {
      // Trigger download tracking
      await UnsplashService.triggerDownload(image.id);
      
      // Use the regular size for most use cases
      const imageUrl = image.urls.regular;
      
      onImageSelect(imageUrl, {
        id: image.id,
        alt: image.alt_description,
        photographer: image.user.name,
        photographerUsername: image.user.username,
        width: image.width,
        height: image.height,
        source: 'unsplash'
      });
      
      onClose();
    } catch (error) {
      console.error('Error selecting Unsplash image:', error);
      alert('Failed to select image. Please try again.');
    } finally {
      setSelectedImageId(null);
    }
  }, [onImageSelect, onClose]);



  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setActiveTab('upload');
    setSearchQuery(initialSearchQuery);
    setUnsplashImages([]);
    setSelectedImageId(null);
    setCurrentPage(1);
    setHasMore(true);
    onClose();
  }, [initialSearchQuery, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl border overflow-hidden ${
            isGlobalDarkMode 
              ? 'bg-gray-900 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${
            isGlobalDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <h2 className={`text-xl font-bold ${
              isGlobalDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{title}</h2>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors ${
                isGlobalDarkMode 
                  ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className={`flex border-b ${
            isGlobalDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? isGlobalDarkMode 
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50' 
                    : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : isGlobalDarkMode 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload from Device</span>
              </div>
            </button>
            
            {showUnsplash && (
              <button
                onClick={() => setActiveTab('unsplash')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'unsplash'
                    ? isGlobalDarkMode 
                      ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50' 
                      : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : isGlobalDarkMode 
                      ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Search className="w-4 h-4" />
                  <span>Search Unsplash</span>
                </div>
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {activeTab === 'upload' ? (
              /* Upload Tab */
              <div className="space-y-6">
                {/* Drop Zone */}
                <div
                  ref={dropZoneRef}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : isGlobalDarkMode 
                        ? 'border-gray-600 hover:border-gray-500 bg-gray-800/30' 
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <Loader className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                      <h3 className={`text-lg font-medium mb-2 ${
                        isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>Uploading...</h3>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        dragActive 
                          ? 'bg-blue-500' 
                          : isGlobalDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                        <Upload className={`w-8 h-8 ${
                          dragActive 
                            ? 'text-white' 
                            : isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                      </div>
                      <h3 className={`text-lg font-medium mb-2 ${
                        isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {dragActive ? 'Drop your image here' : 'Upload an image'}
                      </h3>
                      <p className={`text-sm mb-4 ${
                        isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Drag and drop or click to browse
                      </p>
                      <div className={`text-xs ${
                        isGlobalDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        <p>Supported formats: {acceptedFormats.join(', ')}</p>
                        <p>Maximum size: {maxFileSize}MB</p>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptedFormats.join(',')}
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                />
                
                {/* Device Size Recommendations */}
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg border ${
                  isGlobalDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="text-center">
                    <Monitor className={`w-8 h-8 mx-auto mb-2 ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                    <h4 className={`text-sm font-medium ${
                      isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Desktop</h4>
                    <p className={`text-xs ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>1920×1080px</p>
                  </div>
                  <div className="text-center">
                    <Tablet className={`w-8 h-8 mx-auto mb-2 ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                    <h4 className={`text-sm font-medium ${
                      isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Tablet</h4>
                    <p className={`text-xs ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>1024×768px</p>
                  </div>
                  <div className="text-center">
                    <Smartphone className={`w-8 h-8 mx-auto mb-2 ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`} />
                    <h4 className={`text-sm font-medium ${
                      isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Mobile</h4>
                    <p className={`text-xs ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>375×667px</p>
                  </div>
                </div>
              </div> 
            ) : (
              /* Unsplash Tab */
              <div className="space-y-6">
                {/* Search Bar */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search for images..."
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-colors ${
                      isGlobalDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>

                {/* Loading State */}
                {isSearching && unsplashImages.length === 0 && (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-500" />
                    <span className={`ml-3 ${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Searching images...</span>
                  </div>
                )}

                {/* Images Grid */}
                {unsplashImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {unsplashImages.map((image) => (
                      <motion.div
                        key={image.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative group cursor-pointer rounded-lg overflow-hidden aspect-square"
                        onClick={() => handleUnsplashSelect(image)}
                      >
                        <img
                          src={image.urls.small}
                          alt={image.alt_description || 'Unsplash image'}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          {selectedImageId === image.id ? (
                            <div className="bg-white/90 rounded-full p-2">
                              <Loader className="w-5 h-5 animate-spin text-blue-500" />
                            </div>
                          ) : (
                            <div className="bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Download className="w-5 h-5 text-gray-900" />
                            </div>
                          )}
                        </div>

                        {/* Attribution */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs truncate">
                            Photo by {image.user.name}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Load More Button */}
                {hasMore && unsplashImages.length > 0 && (
                  <div className="text-center">
                    <button
                      onClick={loadMoreImages}
                      disabled={isSearching}
                      className={`px-6 py-3 rounded-lg transition-colors ${
                        isSearching
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {isSearching ? (
                        <div className="flex items-center space-x-2">
                          <Loader className="w-4 h-4 animate-spin" />
                        </div>
                      ) : (
                        'Load More'
                      )}
                    </button>
                  </div>
                )}

                {/* No Results */}
                {!isSearching && searchQuery && unsplashImages.length === 0 && (
                  <div className="text-center py-12">
                    <ImageIcon className={`w-16 h-16 mx-auto mb-4 ${
                      isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <h3 className={`text-lg font-medium mb-2 ${
                      isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>No images found</h3>
                    <p className={`${
                      isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Try searching with different keywords
                    </p>
                  </div>
                )}

                {/* Unsplash Attribution */}
                <div className={`text-center text-xs ${
                  isGlobalDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  <p>
                    Photos provided by{' '}
                    <a 
                      href="https://unsplash.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      Unsplash
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};